import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import {
  renderBarcodeSvg,
  labelBarcodeHeightMm,
  labelBarcodePreviewHeightPx,
  LABEL_BARCODE_PREVIEW_MODULE_WIDTH,
  LABEL_BOTTOM_LINE_HEIGHT_MM,
  LABEL_META_HEIGHT_MM,
  LABEL_META_TO_BAR_GAP_MM,
  LABEL_PANEL_PAD_MM,
  LABEL_TOP_PAD_MM,
} from '../../utils/inventoryLabelBarcode';
import {
  LABEL_PRINT_WIDTH_MM,
  LABEL_PRINT_HEIGHT_MM,
  LABEL_EMPTY_MM,
  LABEL_FOLD_MM,
  LABEL_INFO_LEFT_MM,
  LABEL_BARCODE_LEFT_MM,
  LABEL_INFO_WIDTH_MM,
  LABEL_BARCODE_WIDTH_MM,
} from '../../utils/printInventoryLabels';

/** Preview: empty 44 mm neck + Plata/Bs panel + barcode panel at the strip end. */
export interface InventoryLabelPreviewRow {
  productCodeUnique: string;
  materialLabel: string;
  salePrice: number;
  weightGrams: number | null;
  diameterSize?: string | null;
  workshopCode?: string | null;
}

export function InventoryLabelPreviewMini({ row }: { row: InventoryLabelPreviewRow }) {
  const svgRef = useRef<SVGSVGElement>(null);
  const workshopCode = (row.workshopCode ?? '').trim();
  const talla = (row.diameterSize ?? '').trim();
  const hasWorkshop = workshopCode !== '';
  // Provider code and talla share one line under the bars, so either one shortens them.
  const hasBottomLine = hasWorkshop || talla !== '';
  const barHeightMm = labelBarcodeHeightMm(hasBottomLine);

  useEffect(() => {
    // Render at the slot height so the preview matches what the printer produces.
    renderBarcodeSvg(svgRef.current, row.productCodeUnique ?? '', {
      width: LABEL_BARCODE_PREVIEW_MODULE_WIDTH,
      height: labelBarcodePreviewHeightPx(hasBottomLine),
    });
  }, [row.productCodeUnique, hasBottomLine]);

  const priceStr = `Bs. ${Number(row.salePrice).toLocaleString('es-BO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
  const weight =
    row.weightGrams != null && !Number.isNaN(Number(row.weightGrams))
      ? Number(row.weightGrams).toLocaleString('es-BO', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })
      : '';

  return (
    <Box
      sx={{
        position: 'relative',
        width: `${LABEL_PRINT_WIDTH_MM}mm`,
        height: `${LABEL_PRINT_HEIGHT_MM}mm`,
        overflow: 'hidden',
        bgcolor: '#fff',
        color: '#111827',
        fontFamily: '"Times New Roman", Times, serif',
        flexShrink: 0,
        boxSizing: 'border-box',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          left: `${LABEL_FOLD_MM}mm`,
          top: 0,
          bottom: 0,
          borderLeft: '1px dotted',
          borderColor: 'grey.400',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          left: `${LABEL_EMPTY_MM}mm`,
          top: 0,
          bottom: 0,
          borderLeft: '1px dashed',
          borderColor: 'grey.600',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          left: `${LABEL_BARCODE_LEFT_MM}mm`,
          top: 0,
          bottom: 0,
          width: `${LABEL_BARCODE_WIDTH_MM}mm`,
          overflow: 'hidden',
          px: '0.5mm',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: '0.5mm',
            right: '0.5mm',
            top: `${LABEL_TOP_PAD_MM}mm`,
            height: `${LABEL_META_HEIGHT_MM}mm`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '5.5pt',
            lineHeight: 1,
            overflow: 'hidden',
          }}
        >
          <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
            {row.productCodeUnique}
          </Box>
          {weight ? (
            <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
              {weight}
            </Box>
          ) : null}
        </Box>
        <Box
          sx={{
            position: 'absolute',
            left: '0.5mm',
            right: '0.5mm',
            top: `${LABEL_TOP_PAD_MM + LABEL_META_HEIGHT_MM + LABEL_META_TO_BAR_GAP_MM}mm`,
            height: `${barHeightMm}mm`,
            overflow: 'hidden',
          }}
        >
          <svg
            ref={svgRef}
            preserveAspectRatio="xMinYMin meet"
            style={{
              width: 'auto',
              maxWidth: '100%',
              height: `${barHeightMm}mm`,
              maxHeight: `${barHeightMm}mm`,
              display: 'block',
            }}
          />
        </Box>
        {hasBottomLine ? (
          <Box
            sx={{
              position: 'absolute',
              left: '0.5mm',
              right: '0.5mm',
              bottom: `${LABEL_PANEL_PAD_MM}mm`,
              height: `${LABEL_BOTTOM_LINE_HEIGHT_MM}mm`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.5mm',
              fontSize: '4.5pt',
              lineHeight: 1,
              fontFamily: 'Arial, Helvetica, sans-serif',
              overflow: 'hidden',
            }}
          >
            <Box
              component="span"
              sx={{
                flex: '1 1 auto',
                minWidth: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'clip',
              }}
            >
              {workshopCode}
            </Box>
            {talla !== '' ? (
              <Box
                component="span"
                sx={{ flex: '0 0 auto', fontWeight: 700, whiteSpace: 'nowrap' }}
              >
                {talla}
              </Box>
            ) : null}
          </Box>
        ) : null}
      </Box>
      <Box
        sx={{
          position: 'absolute',
          left: `${LABEL_INFO_LEFT_MM}mm`,
          top: '0.5mm',
          bottom: '0.5mm',
          width: `${LABEL_INFO_WIDTH_MM}mm`,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          overflow: 'hidden',
          px: '1mm',
          boxSizing: 'border-box',
        }}
      >
        <Typography sx={{ fontStyle: 'italic', fontSize: '9pt', lineHeight: 1.1 }} noWrap>
          {row.materialLabel}
        </Typography>
        <Typography sx={{ fontSize: '9pt', lineHeight: 1.1 }} noWrap>
          {priceStr}
        </Typography>
      </Box>
    </Box>
  );
}
